"use client";
import type { ReactNode } from "react";
import type { Control } from "react-hook-form";
import { Controller } from "react-hook-form";
import { MdMemory } from "react-icons/md";
import { FormattedMessage, useIntl } from "react-intl";
import {
  CustomTooltip,
  FormField,
  FormItem,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Slider
} from "@akashnetwork/ui/components";
import { InfoCircle } from "iconoir-react";

import type { SdlBuilderFormValuesType } from "@src/types";
import { memoryUnits } from "@src/utils/akash/units";
import { FormPaper } from "./FormPaper";

type Props = {
  serviceIndex: number;
  children?: ReactNode;
  control: Control<SdlBuilderFormValuesType, any>;
};

export const MemoryFormControl: React.FunctionComponent<Props> = ({ control, serviceIndex }) => {
  const intl = useIntl();
  return (
    <FormField
      control={control}
      name={`services.${serviceIndex}.profile.ram`}
      render={({ field, fieldState }) => (
        <FormPaper>
          <FormItem>
            <div className="flex flex-col items-start lg:flex-row lg:items-center">
              <div className="flex items-center">
                <MdMemory className="mr-2 text-2xl text-muted-foreground" />
                <strong className="text-sm">
                  <FormattedMessage id="sdl.memoryformcontrol.label" defaultMessage="Memory" />
                </strong>

                <CustomTooltip
                  title={
                    <>
                      <FormattedMessage id="sdl.memoryformcontrol.tooltip1" defaultMessage="The amount of memory required for this workload." />
                      <br />
                      <br />
                      <FormattedMessage id="sdl.memoryformcontrol.tooltip2" defaultMessage="The maximum for a single instance is 512 Gi." />
                      <br />
                      <br />
                      <FormattedMessage
                        id="sdl.memoryformcontrol.tooltip3"
                        defaultMessage="The maximum total multiplied by the count of instances is 1024 Gi."
                      />
                    </>
                  }
                >
                  <InfoCircle className="ml-2 text-xs text-muted-foreground" />
                </CustomTooltip>
              </div>

              <div className="mt-2 flex items-center lg:ml-4 lg:mt-0">
                <Input
                  type="number"
                  error={!!fieldState.error}
                  color="secondary"
                  value={field.value || ""}
                  onChange={event => field.onChange(parseFloat(event.target.value))}
                  min={1}
                  step={1}
                  inputClassName="w-[100px]"
                />

                <Controller
                  control={control}
                  name={`services.${serviceIndex}.profile.ramUnit`}
                  defaultValue=""
                  render={({ field }) => (
                    <Select value={field.value?.toLowerCase() || ""} onValueChange={field.onChange}>
                      <SelectTrigger className="ml-1 w-[75px]">
                        <SelectValue placeholder={intl.formatMessage({ id: "sdl.common.selectunit", defaultMessage: "Select unit" })} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          {memoryUnits.map(t => {
                            return (
                              <SelectItem key={t.id} value={t.suffix.toLowerCase()}>
                                {t.suffix}
                              </SelectItem>
                            );
                          })}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </div>

            <Slider
              value={[field.value || 0]}
              min={1}
              max={5120}
              step={1}
              color="secondary"
              aria-label="RAM"
              onValueChange={newValue => field.onChange(newValue[0])}
              className="pt-2"
            />

            <FormMessage />
          </FormItem>
        </FormPaper>
      )}
    />
  );
};
